import { type Chat, type ChatMessage, MAX_MEDIA_BYTES, tallyPoll } from "@rhema/client";
import { Avatar, type ComposerProps, Sheet } from "@rhema/ui";
import {
  type ClipboardEvent,
  type DragEvent,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { errorText } from "../billing/UpgradeLink";
import { toneFor } from "../data/view";
import { useLinks } from "../links/useLinks";
import { describeFile, formatBytes, formatDuration } from "../media/prepare";
import { Preparer } from "../media/preparer";
import { planSend, type TrayItem, trayItems } from "../media/tray";
import { fileEntry, type ViewerEntry, viewerEntries } from "../media/viewerEntries";
import { currentPlatform } from "../platform";
import { useClient, useSession } from "../session/session";
import { AttachSheet, type PickSource } from "./AttachSheet";
import { ForwardSheet } from "./ForwardSheet";
import { mentionIds } from "./Mentions";
import type { ContentActions } from "./MessageContent";
import { nameIn } from "./MessageList";
import { SendTray } from "./SendTray";
import { recordingType, useVoiceRecorder } from "./useVoiceRecorder";

// The viewer is a chunk of its own, fetched the first time a photo is opened.
const MediaViewer = lazy(() => import("../media/MediaViewer"));

const ACCEPT: Record<PickSource, string> = {
  gallery: "image/*,video/*",
  video: "video/*",
  camera: "image/*,video/*",
  document: "*/*",
  audio: "audio/*",
};

const INPUT_LABELS: Record<PickSource, string> = {
  gallery: "Choose photos and videos",
  video: "Choose videos",
  camera: "Take a photo or video",
  document: "Choose documents",
  audio: "Choose audio files",
};

/** The most photos the phone's own picker hands over at once. */
const NATIVE_PICK_LIMIT = 30;

function filesOf(list: FileList | null | undefined): File[] {
  return list ? Array.from(list) : [];
}

function extensionFor(type: string): string {
  if (type.includes("ogg")) return "ogg";
  if (type.includes("mp4")) return "m4a";
  return "webm";
}

/**
 * Everything a conversation needs to send and show photos, files, voice messages, locations,
 * contacts and polls. The conversation spreads the parts into its composer, its container and
 * the message list, and renders the overlays.
 */
export function useChatMedia({
  chat,
  meId,
  messages,
  recipient,
  attachOpen,
  closeAttach,
  replyTo,
  onSent,
  toast,
  threadRoot,
  penielly = true,
}: {
  chat: Chat | undefined;
  meId: string | undefined;
  messages: ChatMessage[];
  recipient: string;
  attachOpen: boolean;
  closeAttach: () => void;
  replyTo: ChatMessage | null;
  /** Called when something is sent, so the conversation can clear its reply bar. */
  onSent: () => void;
  toast: (message: string) => void;
  /** Sends everything as replies in this thread. */
  threadRoot?: string;
  /** Whether Penielly may be used here. */
  penielly?: boolean;
}) {
  const client = useClient();
  const { platform } = useSession();
  const allLinks = useLinks();
  const chatKey = chat?.id ?? "";
  // The server checks each Penielly call against the chat it comes from.
  const links = useMemo(() => (chatKey ? allLinks.inChat(chatKey) : allLinks), [allLinks, chatKey]);
  const inThread = threadRoot ? { threadRoot } : {};
  // A third argument to send only in a thread, so a chat sends exactly as before.
  const sendOpts: [] | [{ threadRoot: string }] = threadRoot ? [{ threadRoot }] : [];
  const navigate = useNavigate();
  const chatId = chat?.id ?? "";
  const [tray, setTray] = useState<TrayItem[] | null>(null);
  const [original, setOriginal] = useState(false);
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  const preparer = useMemo(() => new Preparer(), []);
  const [viewer, setViewer] = useState<{ entries: ViewerEntry[]; start: number } | null>(null);
  const [forwarding, setForwarding] = useState<ChatMessage[] | null>(null);
  const [votesFor, setVotesFor] = useState<ChatMessage | null>(null);
  const [dragging, setDragging] = useState(false);
  const [locating, setLocating] = useState(false);
  const inputs = useRef<Partial<Record<PickSource, HTMLInputElement | null>>>({});
  const nativePick = currentPlatform().pickPhotos;

  /** Puts files in the tray, after what is already there. */
  const offer = useCallback(
    (files: File[], asFiles: boolean) => {
      const fit = files.filter((f) => f.size <= MAX_MEDIA_BYTES);
      const tooBig = files.find((f) => f.size > MAX_MEDIA_BYTES);
      if (tooBig)
        toast(`Files can be up to 2 GB. ${tooBig.name || "That file"} is ${formatBytes(tooBig.size)}.`);
      if (!fit.length) return;
      const added = trayItems(fit, asFiles);
      // Start getting them ready at once, so Send rarely waits.
      for (const item of added) void preparer.get(item, original);
      setTray((current) => [...(current ?? []), ...added]);
    },
    [toast, preparer, original],
  );

  const closeTray = () => {
    setTray(null);
    setOriginal(false);
    setPicked(new Map());
  };

  const sendTray = () => {
    const items = tray ?? [];
    if (!items.length) return;
    const quality = original;
    const mentionsOf = (text: string) => mentionIds(text, picked);
    closeTray();
    const reply = replyTo?.id;
    onSent();
    void (async () => {
      // Ready in the order picked, then sent in that order, so an album arrives in one piece.
      const prepared = await Promise.all(items.map((item) => preparer.get(item, quality)));
      const plan = planSend(items, prepared);
      for (const [i, { item, prepared: p, album }] of plan.entries()) {
        const caption = item.caption.trim();
        const mentions = caption ? mentionsOf(caption) : [];
        void client
          .sendMedia(chatId, p.file, {
            kind: p.kind,
            meta: p.meta,
            ...(caption ? { caption } : {}),
            ...(mentions.length ? { mentions } : {}),
            ...(album ? { album } : {}),
            ...(i === 0 && reply ? { reply_to: reply } : {}),
            ...inThread,
          })
          .catch((e: Error) => toast(errorText(e, platform)));
      }
      for (const item of items) preparer.forget(item.id);
    })();
  };

  const recorder = useVoiceRecorder({
    onError: toast,
    onRecorded: (blob, seconds) => {
      const type = blob.type || recordingType() || "audio/webm";
      const file = new File([blob], `Voice message.${extensionFor(type)}`, { type });
      const reply = replyTo?.id;
      onSent();
      void describeFile(file, "voice").then((meta) =>
        client
          .sendMedia(chatId, file, {
            kind: "voice",
            meta: { ...meta, duration: meta.duration ?? Math.round(seconds * 10) / 10 },
            ...(reply ? { reply_to: reply } : {}),
            ...inThread,
          })
          .catch((e: Error) => toast(errorText(e, platform))),
      );
    },
  });

  const pick = (source: PickSource) => {
    // Phones use their own photo library, which lets many photos be picked at once.
    if (source === "gallery" && nativePick) {
      closeAttach();
      void nativePick(NATIVE_PICK_LIMIT)
        .then((files) => files?.length && offer(files, false))
        .catch(() => toast("Rhema could not open your photos."));
      return;
    }
    inputs.current[source]?.click();
  };

  const shareLocation = () => {
    if (!navigator.geolocation) {
      toast("This browser cannot share a location.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        closeAttach();
        const round = (n: number) => Math.round(n * 1e6) / 1e6;
        void client
          .send(
            chatId,
            {
              type: "location",
              lat: round(pos.coords.latitude),
              lng: round(pos.coords.longitude),
            },
            ...sendOpts,
          )
          .catch(() => toast("That location could not be sent."));
      },
      () => {
        setLocating(false);
        toast("Rhema could not get your location. Check that this site may use your location.");
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  };

  const content: ContentActions | null = useMemo(() => {
    if (!chat) return null;
    const nameOf = (id: string | null) => nameIn(chat, id, meId);
    return {
      chat,
      meId,
      messages,
      openMedia: (m) => {
        const entries = viewerEntries(messages, meId, nameOf);
        const start = entries.findIndex((e) => e.id === m.id);
        if (start >= 0) setViewer({ entries, start });
      },
      openFile: (m) => {
        const entry = fileEntry(m, meId, nameOf);
        if (entry) setViewer({ entries: [entry], start: 0 });
      },
      showVotes: setVotesFor,
      vote: (poll, options) =>
        void client
          .send(chat.id, { type: "vote", poll_id: poll.id, options })
          .catch(() => toast("That vote did not go through.")),
      cancelUpload: (m) => {
        if (m.clientId) client.cancelUpload(chat.id, m.clientId, m.threadRoot ?? undefined);
      },
      retry: (m) => {
        if (m.clientId)
          void client
            .retry(chat.id, m.clientId, m.threadRoot ?? undefined)
            .catch((e: Error) => toast(errorText(e, platform)));
      },
      messagePerson: (userId) =>
        void client
          .createDirect(userId)
          .then((c) => navigate(`/chats/${c.id}`))
          .catch((e: Error) => toast(errorText(e, platform))),
    };
  }, [chat, meId, messages, client, navigate, toast, platform]);

  const composer: Partial<ComposerProps> = {
    onCamera: () => pick("camera"),
    onVoice: recorder.toggle,
    onVoicePress: recorder.press,
    recording: recorder.recording
      ? {
          elapsed: formatDuration(recorder.recording.elapsed),
          cancelling: recorder.recording.cancelling,
          ...(recorder.recording.keyboard ? { onStop: recorder.stop, onCancel: recorder.cancel } : {}),
        }
      : undefined,
  };

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const container = {
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: (e: DragEvent<HTMLDivElement>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDragging(false);
    },
    onDrop: (e: DragEvent<HTMLDivElement>) => {
      const files = filesOf(e.dataTransfer?.files);
      setDragging(false);
      if (!files.length) return;
      e.preventDefault();
      offer(files, false);
    },
    // Pasting files opens the tray, or adds to it when it is open. Pasted text is left alone.
    onPaste: (e: ClipboardEvent<HTMLDivElement>) => {
      // The message box already offered what was pasted into it.
      if (e.defaultPrevented) return;
      const files = filesOf(e.clipboardData?.files);
      if (!files.length) return;
      e.preventDefault();
      offer(files, false);
    },
  };

  const votePoll = votesFor?.content?.type === "poll" ? votesFor.content : null;
  const voters = useMemo(() => {
    if (votesFor?.content?.type !== "poll") return [];
    // Counted again from the live list, so the sheet updates while it is open.
    const tally = tallyPoll(votesFor, messages, meId);
    return votesFor.content.options.map((label, i) => ({ label, people: tally.voters[i] ?? [] }));
  }, [votesFor, messages, meId]);

  const group = chat?.kind === "group" || chat?.kind === "channel";
  const mentionable = useMemo(
    () => (group && chat ? chat.members.filter((m) => m.user.id !== meId) : []),
    [group, chat, meId],
  );

  const overlays: ReactNode = (
    <>
      {(Object.keys(ACCEPT) as PickSource[]).map((source) => (
        <input
          key={source}
          ref={(el) => {
            inputs.current[source] = el;
          }}
          type="file"
          hidden
          aria-label={INPUT_LABELS[source]}
          accept={ACCEPT[source]}
          multiple={source !== "camera"}
          capture={source === "camera" ? "environment" : undefined}
          onChange={(e) => {
            const files = filesOf(e.currentTarget.files);
            e.currentTarget.value = "";
            if (!files.length) return;
            closeAttach();
            offer(files, source === "document" || source === "audio");
          }}
        />
      ))}

      {dragging && (
        <div className="pointer-events-none absolute inset-3 z-30 flex items-center justify-center rounded-panel border-2 border-dashed border-blue bg-linen/90">
          <p className="m-0 font-serif text-xl font-medium">Drop files to send them to {recipient}</p>
        </div>
      )}

      <AttachSheet
        open={attachOpen}
        recipient={recipient}
        penielly={penielly}
        nativePhotos={Boolean(nativePick)}
        onClose={closeAttach}
        onPick={pick}
        locating={locating}
        onLocation={shareLocation}
        onPeniellySend={(items) => {
          closeAttach();
          onSent();
          // Each photo comes to this device from Penielly, then goes out encrypted like any other.
          for (const item of items) {
            void links.penielly
              .sendToChat(
                { sendMedia: (id, file, opts) => client.sendMedia(id, file, { ...opts, ...inThread }) },
                chatId,
                item,
                describeFile,
              )
              .catch((e: Error) => toast(e.message || "That photo could not be sent."));
          }
        }}
        onContact={(person) => {
          closeAttach();
          void client
            .send(chatId, { type: "contact", user_id: person.id, name: person.display_name }, ...sendOpts)
            .catch(() => toast("That contact could not be sent."));
        }}
        onPoll={(poll) => {
          closeAttach();
          void client
            .send(
              chatId,
              {
                type: "poll",
                question: poll.question,
                options: poll.options,
                multiple: poll.multiple,
              },
              ...sendOpts,
            )
            .catch(() => toast("That poll could not be sent."));
        }}
      />

      <SendTray
        items={tray}
        recipient={recipient}
        original={original}
        preparer={preparer}
        members={mentionable}
        onItems={(items) => (items.length ? setTray(items) : closeTray())}
        onOriginal={(on) => {
          setOriginal(on);
          for (const item of tray ?? []) void preparer.get(item, on);
        }}
        onMention={(name, id) => setPicked((m) => new Map(m).set(name, id))}
        onAddMore={() => pick("gallery")}
        onClose={closeTray}
        onSend={sendTray}
      />

      {viewer && (
        <Suspense fallback={null}>
          <MediaViewer
            entries={viewer.entries}
            start={viewer.start}
            chat={chat}
            onClose={() => setViewer(null)}
            onForward={(entry) => {
              const m = messages.find((x) => x.id === entry.id);
              if (!m) return;
              // The viewer closes so the chat picker is on top, as Forward from the menu shows it.
              setViewer(null);
              setForwarding([m]);
            }}
          />
        </Suspense>
      )}

      <ForwardSheet
        messages={forwarding}
        onClose={() => setForwarding(null)}
        onDone={(note) => {
          setForwarding(null);
          toast(note);
        }}
      />

      <Sheet
        open={votePoll !== null}
        onClose={() => setVotesFor(null)}
        fixed={false}
        title="Votes"
        subtitle={votePoll?.question}
        className="px-5 pb-7"
      >
        <ul className="m-0 mt-3 flex list-none flex-col gap-3 p-0">
          {voters.map((option, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: options keep their order
            <li key={i} className="rounded-card border border-line bg-surface px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[15px] font-semibold">{option.label}</span>
                <span className="text-sm text-ink-secondary">
                  {option.people.length} {option.people.length === 1 ? "vote" : "votes"}
                </span>
              </div>
              {option.people.length > 0 && chat && (
                <ul className="m-0 mt-2 flex list-none flex-col gap-1.5 p-0">
                  {option.people.map((id) => (
                    <li key={id} className="flex items-center gap-2 text-sm">
                      <Avatar name={nameIn(chat, id, meId)} tone={toneFor(id)} size={24} />
                      {nameIn(chat, id, meId)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </Sheet>
    </>
  );

  /** Files pasted or dropped into the message box, offered as they would be from Attach. */
  const attach = useCallback((files: File[]) => offer(files, false), [offer]);

  return { composer, container, content, overlays, attach };
}
